import React, { memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { MenuBook as MenuBookIcon } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { usePromptForm } from '../hooks/usePromptForm';
import EditPromptPanel from './EditPromptPanel';
import { makeTagGroupBy } from '../../../shared/utils/tags';
import type { Prompt, PromptTypeType } from '../../../types/backend';
import { PROMPT_TYPE_LABELS } from '../../../shared/constants/promptTypes';

const promptListConfig: EntityListItemConfig<Prompt> = {
  getId: (p) => p.id,
  getPrimaryText: (p) => p.title,
  getSecondaryText: (p) => {
    const typeLabel = PROMPT_TYPE_LABELS[p.type as PromptTypeType] ?? 'Unknown';
    const ownerLabel = p.owner === 'customer' ? 'Customer' : 'Global';
    const status = p.promptVersion && p.promptVersion !== 'DRAFT' ? `v${p.promptVersion}` : 'Unpublished';
    return `${typeLabel} · ${ownerLabel} · ${status}`;
  },
  getSearchText: (p) => [p.name, p.title, ...p.tags].join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

const promptGroupBy = makeTagGroupBy<Prompt>();

interface PromptManagementModalProps {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

const PromptManagementModal = memo<PromptManagementModalProps>(({
  customerId,
  isOpen,
  onClose
}) => {
  const form = usePromptForm({ customerId, isOpen, onClose });

  return (
    <Dialog
      open={isOpen}
      onClose={form.handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
    >
      <ModalHeader icon={MenuBookIcon} title="Manage Prompts" onClose={form.handleClose} desktopRecommended />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        {form.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.showGlobal}
                      onChange={(e) => form.setShowGlobal(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Show Global Prompts</Typography>}
                />
              </Box>

              <EntityListPanel
                entities={form.displayedPrompts}
                selectedEntity={form.selectedPrompt}
                isNew={form.isNewPrompt}
                saving={form.saving}
                config={promptListConfig}
                addButtonText="Add New Prompt"
                emptyStateText="No prompts configured"
                emptyStateHelperText='Click "Add New Prompt" to get started'
                onSelect={form.handleSelect}
                onAddNew={form.handleAddNew}
                searchable
                groupBy={promptGroupBy}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {form.error && (
                <InlineAlert severity="error" onClose={() => form.setError(null)} sx={{ mx: 3, mt: 2 }}>
                  {form.error}
                </InlineAlert>
              )}
              <EditPromptPanel form={form} />
            </Box>
          </>
        )}
      </DialogContent>

      <ModalFooter
        count={form.displayedPrompts.length}
        label={`prompt${form.displayedPrompts.length !== 1 ? 's' : ''} displayed`}
        onClose={form.handleClose}
        py={1.5}
        buttonSize="small"
      />

      <DeleteConfirmDialog
        open={form.showDeleteConfirm}
        title="Delete Prompt?"
        onCancel={form.handleDeleteCancel}
        onConfirm={form.handleDeleteConfirm}
        confirmText="Delete Prompt"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete: <strong>{form.selectedPrompt?.title}</strong> ({form.selectedPrompt?.name})
        </Typography>
        <InlineAlert severity="warning">
          <Typography variant="body2">
            This action cannot be undone. The prompt will be permanently deleted.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>
    </Dialog>
  );
});

PromptManagementModal.displayName = 'PromptManagementModal';

export default PromptManagementModal;
